// Single source of truth for public SEO metadata, structured data, the sitemap,
// robots.txt and the AI knowledge files (generated by scripts/prerender.mjs).
// Only facts already published on this website belong here.

export const SITE_URL = 'https://www.radheybiotech.com';

export const company = {
  legalName: 'Radhey Bio-Tech Pvt. Ltd.',
  name: 'Radhey Bio-Tech',
  alternateNames: ['Radhey Biotech', 'Radhey Bio-Tech Private Limited'],
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/assets/images/logo.png`,
  image: `${SITE_URL}/assets/images/banner.jpg`,
  description:
    'Indian pharmaceutical manufacturing company focused on Active Pharmaceutical Ingredient (API) manufacturing and commercial supply, with CMO and toll manufacturing solutions.',
  emails: ['info@radheybiotech.com', 'aashreya@radheybiotech.com'],
  careersEmail: 'hr@radheybiotech.com',
  phones: {
    office: '+91-124-4604500',
    mobile: '+91-9509013714',
    plant: '0294-3509100',
  },
  registeredOffice: {
    label: 'Registered / Corporate Office',
    streetAddress: '2nd Floor, Tower-A, Building No. 9, DLF Cyber City, Phase-III',
    addressLocality: 'Gurugram',
    addressRegion: 'Haryana',
    postalCode: '122002',
    addressCountry: 'IN',
  },
  manufacturingUnit: {
    label: 'R&D Center and Manufacturing Unit',
    streetAddress:
      'Araji No. 612/613/617-623, Village-Mandap, Patwar Circle-Palanakhurd, Tehsil-Mavli',
    addressLocality: 'Udaipur',
    addressRegion: 'Rajasthan',
    postalCode: '313204',
    addressCountry: 'IN',
  },
  sameAs: [
    'https://www.linkedin.com/company/radhey-bio-tech-private-limited/',
    'https://www.instagram.com/radheybiotech',
  ],
  capabilities: [
    'Active Pharmaceutical Ingredient (API) manufacturing and commercial supply',
    'CMO (contract manufacturing) and toll manufacturing',
    'Technology transfer for customer-provided processes',
    'Process scale-up and optimization',
    'Validation batch coordination and process validation support',
    'Multi-step chemical synthesis, pilot-scale to commercial production',
  ],
  infrastructure: {
    totalReactors: 49,
    stainlessSteelReactors: 31,
    glassLinedReactors: 18,
    annualCapacity: '200 MT',
  },
  quality: [
    'CDSCO-licensed API manufacturing facility',
    'Aligned with Indian GMP requirements under Schedule M',
    'ICH Q7-aligned quality systems',
    'Quality Assurance (QA), Quality Control (QC) and In-Process Quality Control (IPQC)',
    'Analytical capabilities: HPLC, GC, FTIR, UV-Visible spectrophotometry, microbiological analysis, stability studies',
  ],
};

export type ProductInfo = {
  slug: string;
  path: string;
  name: string;
  pharmacopoeia: string[];
  grades: string[];
  // General, well-established description of the molecule (not a company claim).
  classification: string;
};

export const products: ProductInfo[] = [
  {
    slug: 'diclofenac-api',
    path: '/products/diclofenac-api',
    name: 'Diclofenac',
    pharmacopoeia: ['IP', 'BP', 'EP', 'USP'],
    grades: ['Plain'],
    classification: 'Diclofenac is a non-steroidal anti-inflammatory drug (NSAID) active pharmaceutical ingredient.',
  },
  {
    slug: 'aceclofenac-api',
    path: '/products/aceclofenac-api',
    name: 'Aceclofenac',
    pharmacopoeia: ['IP', 'BP', 'EP'],
    grades: ['Micronized', 'Plain'],
    classification: 'Aceclofenac is a non-steroidal anti-inflammatory drug (NSAID) active pharmaceutical ingredient.',
  },
  {
    slug: 'citicoline-api',
    path: '/products/citicoline-api',
    name: 'Citicoline',
    pharmacopoeia: ['IP'],
    grades: ['Plain'],
    classification:
      'Citicoline (cytidine diphosphate-choline, CDP-choline) is an active pharmaceutical ingredient.',
  },
];

export const productDocumentation = [
  'Certificate of Analysis (COA)',
  'Product Specifications',
  'Technical Data Sheet (TDS)',
  'Material Safety Data Sheet (MSDS)',
  'Method of Analysis (MoA)',
  'Stability Data',
  'DMF, where applicable',
  'Batch Traceability',
  'Regulatory Documentation',
];

export type RouteSeo = {
  path: string;
  title: string;
  description: string;
  // Visible breadcrumb trail below Home, used for BreadcrumbList schema.
  breadcrumb?: { name: string; path: string }[];
  pageType?: 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage';
  product?: ProductInfo;
  indexable: boolean;
  inSitemap: boolean;
};

const productRoutes: RouteSeo[] = products.map((product) => ({
  path: product.path,
  title: `${product.name} API Manufacturer in India (${product.pharmacopoeia.join(' / ')}) | Radhey Bio-Tech`,
  description: `${product.name} API (${product.pharmacopoeia.join(' / ')}, ${product.grades.join(' / ')} grade) manufactured and supplied by Radhey Bio-Tech from its API facility in Udaipur, Rajasthan. COA, TDS, MSDS and regulatory documentation available.`,
  breadcrumb: [
    { name: 'Products', path: '/products/our-products' },
    { name: `${product.name} API`, path: product.path },
  ],
  product,
  indexable: true,
  inSitemap: true,
}));

export const routes: RouteSeo[] = [
  {
    path: '/',
    title: 'API Manufacturer in India | Radhey Bio-Tech Pvt. Ltd.',
    description:
      'Radhey Bio-Tech is a pharmaceutical API manufacturer in India with a 200 MT facility in Udaipur, Rajasthan, supplying Diclofenac, Aceclofenac and Citicoline APIs plus CMO and toll manufacturing.',
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/about',
    title: 'About Radhey Bio-Tech | Pharmaceutical API Manufacturer, Udaipur',
    description:
      'Radhey Bio-Tech Pvt. Ltd. manufactures Active Pharmaceutical Ingredients in Udaipur, Rajasthan, with 49 reactors, 200 MT annual capacity, ICH Q7-aligned quality systems and in-house analytics.',
    breadcrumb: [{ name: 'About Us', path: '/about' }],
    pageType: 'AboutPage',
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/mission-vision',
    title: 'Mission, Vision & Core Values | Radhey Bio-Tech',
    description:
      'The mission, vision and core values guiding Radhey Bio-Tech: quality and compliance, manufacturing excellence, technical expertise and long-term pharmaceutical partnerships.',
    breadcrumb: [{ name: 'Mission, Vision & Core Values', path: '/mission-vision' }],
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/directors',
    title: 'Directors & Leadership | Radhey Bio-Tech',
    description:
      'Meet the directors of Radhey Bio-Tech Pvt. Ltd., leading quality-driven API manufacturing, operational excellence and long-term partnerships with pharmaceutical companies.',
    breadcrumb: [{ name: 'Directors', path: '/directors' }],
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/solutions',
    title: 'API Manufacturing, CMO & Toll Manufacturing Solutions | Radhey Bio-Tech',
    description:
      'Contract API manufacturing, CMO and toll manufacturing, technology transfer, process scale-up, validation batches and long-term commercial supply from Radhey Bio-Tech in India.',
    breadcrumb: [{ name: 'Solutions', path: '/solutions' }],
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/products/our-products',
    title: 'API Product Portfolio: Diclofenac, Aceclofenac, Citicoline | Radhey Bio-Tech',
    description:
      'Radhey Bio-Tech API portfolio: Diclofenac (IP/BP/EP/USP), Aceclofenac (IP/BP/EP, micronized and plain) and Citicoline (IP), with full quality and regulatory documentation.',
    breadcrumb: [{ name: 'Products', path: '/products/our-products' }],
    pageType: 'CollectionPage',
    indexable: true,
    inSitemap: true,
  },
  ...productRoutes,
  {
    path: '/gallery',
    title: 'Gallery | Radhey Bio-Tech API Manufacturing Facility',
    description:
      'Photos of the Radhey Bio-Tech API manufacturing facility, R&D center and operations in Udaipur, Rajasthan.',
    breadcrumb: [{ name: 'Gallery', path: '/gallery' }],
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/news-research',
    title: 'News & Research | Radhey Bio-Tech',
    description:
      'Latest news, updates and research publications from Radhey Bio-Tech Pvt. Ltd., an API manufacturer in India.',
    breadcrumb: [{ name: 'News & Research', path: '/news-research' }],
    pageType: 'CollectionPage',
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/career',
    title: 'Careers in Pharmaceutical Manufacturing | Radhey Bio-Tech',
    description:
      'Build your career at Radhey Bio-Tech in pharmaceutical manufacturing, quality and technical operations. See openings on LinkedIn or email your CV to hr@radheybiotech.com.',
    breadcrumb: [{ name: 'Career', path: '/career' }],
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/contact',
    title: 'Contact Radhey Bio-Tech | API Manufacturer, Gurugram & Udaipur',
    description:
      'Contact Radhey Bio-Tech for API enquiries, quotations, samples, documentation and CMO or toll manufacturing. Corporate office in Gurugram, manufacturing unit in Udaipur.',
    breadcrumb: [{ name: 'Contact', path: '/contact' }],
    pageType: 'ContactPage',
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/terms-and-conditions',
    title: 'Terms and Conditions | Radhey Bio-Tech',
    description: 'Terms and conditions for using the Radhey Bio-Tech Pvt. Ltd. website.',
    breadcrumb: [{ name: 'Terms and Conditions', path: '/terms-and-conditions' }],
    indexable: true,
    inSitemap: true,
  },
  {
    path: '/privacy-policy',
    title: 'Privacy Policy | Radhey Bio-Tech',
    description: 'How Radhey Bio-Tech Pvt. Ltd. collects, uses and protects information submitted through this website.',
    breadcrumb: [{ name: 'Privacy Policy', path: '/privacy-policy' }],
    indexable: true,
    inSitemap: true,
  },
  // Staff-only pages: rendered for logged-in staff, never indexed.
  { path: '/my-leads', title: 'My Leads | Radhey Bio-Tech', description: '', indexable: false, inSitemap: false },
  { path: '/admin-details', title: 'Admin | Radhey Bio-Tech', description: '', indexable: false, inSitemap: false },
  {
    path: '/news-research/add-content',
    title: 'Add Content | Radhey Bio-Tech',
    description: '',
    indexable: false,
    inSitemap: false,
  },
];

export const notFoundRoute: RouteSeo = {
  path: '/404',
  title: 'Page Not Found | Radhey Bio-Tech',
  description: 'The page you are looking for could not be found.',
  indexable: false,
  inSitemap: false,
};

const newsArticleRoute = (path: string): RouteSeo => ({
  path,
  title: 'News & Research Article | Radhey Bio-Tech',
  description: 'News and research update from Radhey Bio-Tech Pvt. Ltd., an API manufacturer in India.',
  breadcrumb: [
    { name: 'News & Research', path: '/news-research' },
    { name: 'Article', path },
  ],
  indexable: true,
  inSitemap: false,
});

export const normalizePath = (pathname: string) => {
  const path = pathname.replace(/\/+$/, '');
  return path === '' ? '/' : path;
};

export const findRoute = (pathname: string): RouteSeo => {
  const path = normalizePath(pathname);
  const route = routes.find((r) => r.path === path);
  if (route) return route;
  if (/^\/news-research-details\/[^/]+$/.test(path)) return newsArticleRoute(path);
  return notFoundRoute;
};
